<script setup>
import LiveSports from './Partials/LiveSports.vue';
import MatchListItem from '@/components/Match/MatchListItem.vue';
import MatchGroupHeader from '@/components/Match/MatchGroupHeader.vue';
import MatchStats from '@/components/Match/MatchStats.vue';
import { computed, ref , watch, onMounted, onUnmounted} from 'vue';
import { storeToRefs } from 'pinia';
import { debounce, safeTranslate } from '@/helpers';
import { useRoute, useRouter } from 'vue-router';
import { useFixturesStore } from '@/stores/fixtures';
import { useI18n } from 'vue-i18n';
import mainMarkets from '@/mainMarkets.js' 

const { t, te } = useI18n()
const fixtures = useFixturesStore()
const { getLiveSports, getLiveEventsBySport } = storeToRefs(fixtures)

const route = useRoute()
const router = useRouter()

const eventToShowStats = ref(undefined)
const search = ref('')
const result = ref()
const hasSearched = ref(false)
const liveScoreboardsMap = ref({})

const visibleLiveEvents = ref(new Set())
const liveSelectionsMap = ref({})   // selections for live events
let observer

const activeSport = computed(() => {
  const sportId = route.query.s;
  if (sportId) {
    return getLiveSports.value.find(s => s.si == sportId);
  }
  return getLiveSports.value[0] || null;
});

const liveMarketId = computed(() => {
  const sportId = activeSport.value?.si
  return mainMarkets[sportId]?.[1] || null
})

const submit = debounce(() => {
  if (!search.value.trim()) {
    hasSearched.value = false;
    result.value = undefined; 
    return;
  }
  result.value = fixtures.searchForLiveEvents(search.value, activeSport.value);
  hasSearched.value = true;
}, 500);

watch(search, () => submit());

const changeActiveSport = (sport) => {
  router.push(`/in-play?s=${sport.si}`)
}
const showEventStats = (eid) => {
  eventToShowStats.value = eid
}

const observeEvents = () => {
  if (observer) observer.disconnect();

  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const eventId = Number(entry.target.dataset.i);
        if (entry.isIntersecting) {
          visibleLiveEvents.value.add(eventId);
        } else {
          visibleLiveEvents.value.delete(eventId);
        }
      });
    },
    { root: null, threshold: 0.1 }
  );

  document.querySelectorAll(".live-event-item").forEach((el) => {
    observer.observe(el);
  });
};

onMounted(() => {
  observeEvents();
})

onUnmounted(() => {
  if (observer) observer.disconnect()
})

watch(activeSport, () => {
  visibleLiveEvents.value.clear()  
  liveSelectionsMap.value = {}     
  setTimeout(() => observeEvents(), 50)
})

watch(result, () => {
  visibleLiveEvents.value.clear()
  liveSelectionsMap.value = {}
  setTimeout(() => observeEvents(), 50)
})

watch(
  () => getLiveEventsBySport.value(activeSport.value?.si) || [],
  async (newComps, oldComps) => {
    if (!Array.isArray(newComps)) return

    setTimeout(() => observeEvents(), 50)

    //find ids which are not in seelctions
    const allIds = newComps.flatMap(c => c.events.map(e => e.i))
    const newIds = allIds.filter(
      id => !(id in liveSelectionsMap.value)
    )

    if (!newIds.length) return

    //take only id which are on screen
    const visibleNewIds = Array.from(visibleLiveEvents.value).filter(id => newIds.includes(id))
    if (!visibleNewIds.length) return

    const sportId = activeSport.value?.si
    const marketId = mainMarkets[sportId]?.[1]
    const markets = marketId ? [marketId] : undefined

    // Fetch selections 
    const res = await fixtures.fetchSelections(visibleNewIds, "live", markets)
    for (const ev of res) {
      liveSelectionsMap.value[ev.fi] = (ev.m || []).map(m => ({
        ...m,
        key: m.ui
      }))
    }
    // Fetch scoreboards 
    const sbs = await fixtures.fetchScoreboards(visibleNewIds)
    if (Array.isArray(sbs)) {
      sbs.forEach(sb => (liveScoreboardsMap.value[sb.fi] = sb))
    }
  },
  { deep: true }
)

 watch(
   visibleLiveEvents,
   async (ids) => {
     if (ids.size === 0) return
    const sportId = activeSport.value?.si
    const liveMarketId = mainMarkets[sportId]?.[1] 

    const markets = liveMarketId ? [liveMarketId] : undefined
    const res = await fixtures.fetchSelections(Array.from(ids), "live", markets)

    await fixtures.fetchScoreboards(Array.from(ids)).then(res => {
      if (Array.isArray(res)) {
        res.forEach(sb => {
          liveScoreboardsMap.value[sb.fi] = sb
        })
      }
    })

    for (const ev of res) {
       liveSelectionsMap.value[ev.fi] = ev.m || []
     }
   },
   { deep: true }
 )
const regionTranslation = (event) => { 
  return safeTranslate('regions', event.ri, t, te , event.rn)
}

</script>

<template>
    <LiveSports
        :sports="getLiveSports"
        :active="activeSport?.si"
        @selected="changeActiveSport"
    />
    <div class="flex flex-1">
        <section :class="`lv-SportEvents lv-SportEvents-${activeSport?.si}`">
            <div class="py-3 px-2 xl:px-6 flex items-center justify-between">
                <h3 class="text-xl text-white font-bold italic">
                  {{ $te(`sports.${activeSport.si}`) ? $t(`sports.${activeSport.si}`) : activeSport.sn }}
                </h3>
                <div class="SearchInput" v-if="getLiveEventsBySport(activeSport.si).length">
                    <div class="SearchInput_Icon" />
                    <input
                        class="SearchInput_Field"
                        placeholder="Search..."
                        type="search"
                        v-model="search"
                    />
                </div>
            </div>
            <template v-if="!hasSearched">
                <template v-for="comp in getLiveEventsBySport(activeSport.si)">
                        <MatchGroupHeader 
                            :title="`${regionTranslation(comp.events[0])} - ${comp?.cn}`"
                            :sport="activeSport.si"
                        >
                            <template #title>
                                <div class="flex items-center">
                                    <img :src="`/loc/${comp.events[0].ri}.svg`" class="w-4 h-4 rounded-full mr-2" alt="">
                                    {{ regionTranslation(comp.events[0])}} - {{ comp?.cn}}
                                </div>
                            </template>
                        </MatchGroupHeader>
                        <ul class="list-none divide-y divide-neutral-500/25">
                            <li 
                                v-for="event in comp.events"  
                                :key="event.i"
                                class="live-event-item"
                                :data-i="event.i"
                            > 
                                <MatchListItem  
                                    :key="event.i"
                                    :event="event"
                                    :selections="liveSelectionsMap[event.i] || []"
                                    :live="true"
                                    :market="liveMarketId"
                                    :scoreboard="liveScoreboardsMap[event.i] || null"
                                    @stats="showEventStats"
                                />

                            </li>
                        </ul>
                </template>
            </template>
            <template v-if="hasSearched">
                <ul>
                  <li 
                    v-for="event in result"
                    :key="event.i"
                    class="live-event-item"
                    :data-i="event.i"
                    >
                        <MatchListItem
                            :event="event"
                            :selections="liveSelectionsMap[event.i] || []"
                            :live="true"
                            :market="liveMarketId"
                            :scoreboard="liveScoreboardsMap[event.i] || null"
                            @stats="showEventStats"
                        />
                    </li>
                </ul> 
            </template>
        </section>
    </div>
</template>

<style scoped>
.lv-SportEvents {
    @apply relative flex-1;
}

.lv-SportEvents-1 {background-image: linear-gradient(160deg,#364D3C 0%, var(--color-grey3) 400px)}
.lv-SportEvents-3 {background-image: linear-gradient(160deg,#3F4D32 0%, var(--color-grey3) 400px)}
.lv-SportEvents-2 {background-image: linear-gradient(160deg,#4D4432 0%, var(--color-grey3) 400px)}
.lv-SportEvents-19 {background-image: linear-gradient(160deg,#474D36 0%, var(--color-grey3) 400px)}
.lv-SportEvents-17 {background-image: linear-gradient(160deg,#4D2725 0%, var(--color-grey3) 400px)}
.lv-SportEvents-5 {background-image: linear-gradient(160deg,#36444D 0%, var(--color-grey3) 400px)}
.lv-SportEvents-6 {background-image: linear-gradient(160deg,#36414D 0%, var(--color-grey3) 400px)}
.lv-SportEvents-4 {background-image: linear-gradient(160deg,#4D4032 0%, var(--color-grey3) 400px)}

.SearchInput {
    background-color: hsla(0,0%,100%,.1);
    border-radius: 50px;
    display: flex;
    @apply px-3;
}

.SearchInput_Icon {
    mask-image: url("/search.svg");
    mask-size: 18px 18px;
    mask-repeat: no-repeat;
    mask-position: center;
    min-width: 18px;
    background-color: #fff;
    z-index: 20;
    display: flex;
    flex-direction: column
}

.SearchInput_Field {
    @apply flex-1 text-sm !bg-transparent placeholder:text-white font-bold text-white py-2.5 border-0;
    @apply focus:ring-0;
}
</style>